import type { QueryKey } from '@tanstack/react-query';

import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { ledgerQueryKeys } from './useGeneralLedger';
import { queryKeys } from './queryKeys';
import {
  bankIncomingService,
  type BankIncomingFilters,
  type BankIncomingListResponse,
  type BankIncomingScope,
  type OpenItemFilters,
  type OpenItemListResponse,
  type OpenItemMatchPayload,
  type SplitAllocationPayload,
  type StatementImportOptions,
  type StatementImportResult,
} from '@/services/bankIncomingService';
import type {
  BankIncomingAdminLine,
  BankIncomingAuditEntry,
  BankIncomingSummary,
  BankLineCostTemplate,
} from '@/types/bank-reconciliation';
import type { FundCostDesignate } from '@/types/fund-costs';
import type { DealCostFromBankLineCreate } from '@/types/deal-costs';
import type { APIError } from '@/utils/api-client';

// The admin module and each fund-scoped one read different endpoints, so they
// get their own cache branch; invalidation still happens on the shared root.
const scopeKey = (scope?: BankIncomingScope) => scope?.fundId ?? 'admin';

export const bankIncomingQueryKeys = {
  all: ['bank-incoming'] as const,
  scope: (scope?: BankIncomingScope) =>
    [...bankIncomingQueryKeys.all, scopeKey(scope)] as const,
  list: (filters: BankIncomingFilters, scope?: BankIncomingScope) =>
    [...bankIncomingQueryKeys.scope(scope), 'list', filters] as const,
  summary: (filters: BankIncomingFilters, scope?: BankIncomingScope) =>
    [...bankIncomingQueryKeys.scope(scope), 'summary', filters] as const,
  detail: (incomingId: string, scope?: BankIncomingScope) =>
    [...bankIncomingQueryKeys.scope(scope), 'detail', incomingId] as const,
  audit: (incomingId: string, scope?: BankIncomingScope) =>
    [...bankIncomingQueryKeys.scope(scope), 'audit', incomingId] as const,
  costTemplate: (incomingId: string, scope: BankIncomingScope) =>
    [
      ...bankIncomingQueryKeys.scope(scope),
      'cost-template',
      incomingId,
    ] as const,
  openItems: (
    incomingId: string,
    filters: OpenItemFilters,
    scope?: BankIncomingScope
  ) =>
    [
      ...bankIncomingQueryKeys.scope(scope),
      'open-items',
      incomingId,
      filters,
    ] as const,
};

export const useBankIncomingList = (
  filters: BankIncomingFilters = {},
  scope?: BankIncomingScope
) =>
  useApiQuery<BankIncomingListResponse>(
    bankIncomingQueryKeys.list(filters, scope),
    () => bankIncomingService.list(filters, scope),
    { staleTime: 30 * 1000 }
  );

export const useBankIncomingSummary = (
  filters: BankIncomingFilters = {},
  scope?: BankIncomingScope
) =>
  useApiQuery<BankIncomingSummary>(
    bankIncomingQueryKeys.summary(filters, scope),
    () => bankIncomingService.summary(filters, scope),
    { staleTime: 30 * 1000 }
  );

// One staged line on its own, for a screen that knows its id but never listed
// it - a ?match= link opened cold, or a capital call reading the transaction
// behind an investor's payment.
export const useBankIncomingLine = (
  incomingId: string | null,
  scope?: BankIncomingScope
) =>
  useApiQuery<BankIncomingAdminLine>(
    bankIncomingQueryKeys.detail(incomingId ?? '', scope),
    () => bankIncomingService.get(incomingId as string, scope),
    { enabled: Boolean(incomingId), staleTime: 30 * 1000 }
  );

export const useBankIncomingAudit = (
  incomingId: string | null,
  scope?: BankIncomingScope
) =>
  useApiQuery<BankIncomingAuditEntry[]>(
    bankIncomingQueryKeys.audit(incomingId ?? '', scope),
    () => bankIncomingService.audit(incomingId as string, scope),
    { enabled: Boolean(incomingId) }
  );

export const useBankIncomingCostTemplate = (
  incomingId: string,
  scope: BankIncomingScope
) =>
  useApiQuery<BankLineCostTemplate>(
    bankIncomingQueryKeys.costTemplate(incomingId, scope),
    () => bankIncomingService.costTemplate(incomingId, scope)
  );

// Only fetched while the Match Manually dialog is open. What is open changes
// the moment any line is settled, so this is not cached across a decision.
export const useBankIncomingOpenItems = (
  incomingId: string | null,
  filters: OpenItemFilters = {},
  scope?: BankIncomingScope
) =>
  useApiQuery<OpenItemListResponse>(
    bankIncomingQueryKeys.openItems(incomingId ?? '', filters, scope),
    () => bankIncomingService.openItems(incomingId as string, filters, scope),
    { enabled: Boolean(incomingId), staleTime: 0 }
  );

// Every decision invalidates the whole module: accepting one line changes the
// backlog totals and can change what the engine would suggest for the others.
// The root key is scope-free on purpose - a decision taken in a fund also
// changes what the admin module shows, and vice versa.
//
// Accepting goes through the same write path as a manual match, so it also
// settles a bank transaction and moves the confirmation on the fund
// transaction. The fund-scoped ledger and fund-transaction caches read that
// same state under their own keys, and neither shares a prefix with
// ['bank-incoming'] - without them here the Bank Entries tab and the
// transaction detail keep serving pre-decision data for a full stale window.
const SETTLEMENT_KEYS: QueryKey[] = [
  bankIncomingQueryKeys.all,
  ledgerQueryKeys.all,
  queryKeys.fundTransactions.all,
  queryKeys.carryFunds.payments(),
];

// Work that only changes what sits in the inbox - rejecting a suggestion, or
// importing a statement - leaves the ledger and the fund transaction alone,
// but both incoming views show it.
const SUGGESTION_KEYS: QueryKey[] = [
  bankIncomingQueryKeys.all,
  [...ledgerQueryKeys.all, 'bank-incoming'],
];

const useDecision = <TArgs, TResult>(
  action: (args: TArgs) => Promise<TResult>,
  invalidateKeys: QueryKey[],
  successMessage: string | ((data: TResult) => string)
) =>
  useApiMutation<TResult, APIError, TArgs>(action, {
    invalidateKeys,
    successMessage,
  });

export const useAcceptBankIncomingSuggestion = (scope?: BankIncomingScope) =>
  useDecision(
    (incomingId: string) => bankIncomingService.accept(incomingId, scope),
    SETTLEMENT_KEYS,
    'Suggestion accepted - bank confirmation recorded'
  );

export const useRejectBankIncomingSuggestion = (scope?: BankIncomingScope) =>
  useDecision(
    ({ incomingId, reason }: { incomingId: string; reason?: string }) =>
      bankIncomingService.reject(incomingId, reason, scope),
    SUGGESTION_KEYS,
    'Suggestion rejected'
  );

export const useMatchBankIncomingOpenItem = (scope?: BankIncomingScope) =>
  useDecision(
    ({
      incomingId,
      target,
    }: {
      incomingId: string;
      target: OpenItemMatchPayload;
    }) => bankIncomingService.matchOpenItem(incomingId, target, scope),
    [...SETTLEMENT_KEYS, queryKeys.portfolio.all],
    'Matched - bank confirmation recorded'
  );

// Books nothing, so only the inbox views change.
export const useMarkBankIncomingInternalTransfer = (
  scope?: BankIncomingScope
) =>
  useDecision(
    (incomingId: string) =>
      bankIncomingService.markInternalTransfer(incomingId, scope),
    SUGGESTION_KEYS,
    'Marked as internal transfer'
  );

export const useBookBankIncomingAsDealCost = (fundId: string) =>
  useDecision(
    ({
      incomingId,
      data,
    }: {
      incomingId: string;
      data: DealCostFromBankLineCreate;
    }) => bankIncomingService.bookAsDealCost(fundId, incomingId, data),
    [...SETTLEMENT_KEYS, queryKeys.dealCosts.all],
    'Booked as deal cost'
  );

/**
 * Places amounts across several open items in one call. What the allocations
 * leave behind stays on the line, so the message says which of the two
 * happened rather than always claiming the line is settled.
 */
export const useAllocateBankIncoming = (scope?: BankIncomingScope) =>
  useDecision(
    ({
      incomingId,
      allocations,
    }: {
      incomingId: string;
      allocations: SplitAllocationPayload[];
    }) => bankIncomingService.split(incomingId, allocations, scope),
    [...SETTLEMENT_KEYS, queryKeys.drawdowns.all],
    (line) =>
      line.unapplied_amount > 0
        ? 'Allocated - the rest of the line is still in the inbox'
        : 'Allocated - the line is fully accounted for'
  );

export const useUnlinkBankIncomingAllocation = (scope?: BankIncomingScope) =>
  useDecision(
    ({
      incomingId,
      allocationId,
    }: {
      incomingId: string;
      allocationId: string;
    }) => bankIncomingService.unlinkAllocation(incomingId, allocationId, scope),
    [...SETTLEMENT_KEYS, queryKeys.drawdowns.all],
    'Allocation unlinked - that much of the line is back in the inbox'
  );

export const useBulkAcceptBankIncoming = (scope?: BankIncomingScope) =>
  useDecision(
    (incomingIds: string[]) =>
      bankIncomingService.bulkAccept(incomingIds, scope),
    SETTLEMENT_KEYS,
    // The endpoint accepts what it can and reports the rest per line; saying
    // only how many went through would read as a clean run.
    (result) => {
      const failed = Object.keys(result.failed).length;
      const accepted = `${result.accepted.length} line${result.accepted.length === 1 ? '' : 's'} accepted`;
      return failed === 0 ? accepted : `${accepted}, ${failed} failed`;
    }
  );

// Decisions no longer re-suggest the other lines; this does, on demand.
export const useRefreshBankIncomingSuggestions = (scope: BankIncomingScope) =>
  useDecision(
    () => bankIncomingService.refreshSuggestions(scope),
    SUGGESTION_KEYS,
    'Suggestions refreshed'
  );

// Not a decision: the panel renders the run's own summary, and a preview books
// nothing, so a success toast would claim more than the run did.
export const useImportBankStatement = (scope: BankIncomingScope) =>
  useApiMutation<
    StatementImportResult,
    APIError,
    { file: File; options: StatementImportOptions }
  >(
    ({ file, options }) =>
      bankIncomingService.importStatement(file, options, scope),
    { invalidateKeys: SUGGESTION_KEYS, showSuccessToast: false }
  );

/**
 * Settle one investor's notice from one staged bank line. A split of one,
 * down the same applier the inbox uses.
 */
export const useSettleNoticeFromBankLine = (scope: BankIncomingScope) =>
  useDecision(
    ({
      incomingId,
      amount,
      drawdownInvestorId,
    }: {
      incomingId: string;
      amount: number;
      drawdownInvestorId: string;
    }) =>
      bankIncomingService.split(
        incomingId,
        [{ amount, drawdown_investor_id: drawdownInvestorId }],
        scope
      ),
    // Settling changes the investor's paid amount, the drawdown totals, and
    // the ledger's mirrored bank entry.
    [...SETTLEMENT_KEYS, queryKeys.drawdowns.all],
    'Payment recorded from the bank line'
  );

export const useDesignateFundCost = (scope: BankIncomingScope) =>
  useDecision(
    ({ incomingId, data }: { incomingId: string; data: FundCostDesignate }) =>
      bankIncomingService.designateFundCost(incomingId, data, scope),
    [...SETTLEMENT_KEYS, queryKeys.fundCosts.all],
    'Fund cost created - bank confirmation recorded'
  );
